<script setup lang="ts">
import type { IconName } from '/#/components/base/icon/icon'
import type { PlaceLoadItems, PlaceLoadProps } from '/#/components/base/placeload'
import type { ComputedRef, Ref } from 'vue'

import isUndefined from 'lodash-es/isUndefined'
import range from 'lodash-es/range'

import { generateArrayToGridTemplateArea } from '/@/utils/formatter'

const props = withDefaults(defineProps<PlaceLoadProps>(), {
  template: () => [],
  repeat: 1,
  templateColumns: undefined,
})

const isReadyTemplate: Ref<boolean> = ref(false)

const readyTemplates: Ref<string[]> = ref([
  'form',
  'page',
  'card-row',
  'chart',
  'heatmap',
  'progress-chart',
  'pie-chart',
  'bar-chart',
])

const items: Ref<PlaceLoadItems[]> = ref([])
items.value = props.type.split(',').map((item) => {
  const [type, count] = item.split('@')
  const result: string | undefined = readyTemplates.value.find(
    (template: string) => template === type,
  )
  if (result) isReadyTemplate.value = true
  return {
    type,
    count: count ? parseInt(count, 10) : 1,
  }
}) as PlaceLoadItems[]

const readyTemplatesComponents: Ref<Recordable> = ref({
  form: [
    { type: 'input', count: 6 },
    { type: 'button', count: 2 },
  ],
  page: [{ type: 'card', count: 8 }],
  'card-row': [{ type: 'card', count: 2 }],
  chart: [{ type: 'full', count: 50 }],
  heatmap: [{ type: 'heatmap', count: 180 }],
  'progress-chart': [{ type: 'progress-chart', count: 50 }],
  'pie-chart': [{ type: 'pie', count: 1 }],
  'bar-chart': [
    { type: 'y-axis', count: 7 },
    { type: 'bar', count: 12 },
    { type: 'x-label', count: 12 },
  ],
})

const loaders: Ref<PlaceLoadItems[]> = ref(
  isReadyTemplate.value ? readyTemplatesComponents.value[props.type] : items.value,
)

const readyTemplatesGrid: Ref<Recordable<string[]>> = ref({
  form: [
    'input1 input2 input2 input2',
    'input3 input3 input3 input3',
    'input4 input4 input5 input5',
    'input6 input6 input6 input6',
    '. . button1 button2',
  ],
  page: [
    'card1 card1 card2 card2',
    'card3 card4 card4 card4',
    'card5 card5 card5 card6',
    'card7 card8 card8 card8',
  ],
  'card-row': ['card1 card2'],
  chart: [
    range(1, 51)
      .map((i) => `full${i}`)
      .join(' '),
  ],
  heatmap: range(0, 25).map((row) =>
    range(1, 31)
      .map((col) => `heatmap${row * 30 + col}`)
      .join(' '),
  ),
  'progress-chart': [
    range(1, 11)
      .map((i) => `progress-chart${i}`)
      .join(' '),
  ],
  'pie-chart': ['pie1'],
  'bar-chart': [
    'y-axis1 bar1 bar2 bar3 bar4 bar5 bar6 bar7 bar8 bar9 bar10 bar11 bar12',
    'y-axis2 bar1 bar2 bar3 bar4 bar5 bar6 bar7 bar8 bar9 bar10 bar11 bar12',
    'y-axis3 bar1 bar2 bar3 bar4 bar5 bar6 bar7 bar8 bar9 bar10 bar11 bar12',
    'y-axis4 bar1 bar2 bar3 bar4 bar5 bar6 bar7 bar8 bar9 bar10 bar11 bar12',
    'y-axis5 bar1 bar2 bar3 bar4 bar5 bar6 bar7 bar8 bar9 bar10 bar11 bar12',
    'y-axis6 bar1 bar2 bar3 bar4 bar5 bar6 bar7 bar8 bar9 bar10 bar11 bar12',
    'y-axis7 bar1 bar2 bar3 bar4 bar5 bar6 bar7 bar8 bar9 bar10 bar11 bar12',
    'y-axis8 bar1 bar2 bar3 bar4 bar5 bar6 bar7 bar8 bar9 bar10 bar11 bar12',
    'y-axis9 bar1 bar2 bar3 bar4 bar5 bar6 bar7 bar8 bar9 bar10 bar11 bar12',
    'y-axis10 bar1 bar2 bar3 bar4 bar5 bar6 bar7 bar8 bar9 bar10 bar11 bar12',
    '. x-label1 x-label2 x-label3 x-label4 x-label5 x-label6 x-label7 x-label8 x-label9 x-label10 x-label11 x-label12',
  ],
})

/**
 * @name gridTemplateColumns
 * @description Defines grid columns
 */
const gridTemplateColumns: ComputedRef<string> = computed(
  () => props.templateColumns || `repeat('auto-fill', minmax(70px, 1fr))`,
)

/**
 * @name gridTemplateAreas
 * @description Defines grid template areas is template is ready-template
 */
const gridTemplateAreas: ComputedRef<string> = computed(() =>
  generateArrayToGridTemplateArea(
    isReadyTemplate.value ? readyTemplatesGrid.value[props.type] : props.template,
  ),
)

/**
 * @name display
 * @description If the template is not empty, generate a grid template area
 */
const display: ComputedRef<string> = computed(() => {
  if (['progress-chart', 'pie-chart'].includes(props.type)) {
    return 'flex'
  }
  return gridTemplateAreas.value ? 'grid' : 'flex'
})

/**
 * @method generateIcon
 * @description Generates icon for some skeleton types.
 * @param item
 */
function generateIcon(item: Recordable): Undefinable<IconName> {
  switch (item.type) {
    case 'image':
      return 'image-picture-modern-5-square'
    case 'video':
      return 'camera-video-1'
    case 'attachment':
      return 'attachment'
    default:
      return undefined
  }
}
</script>

<template>
  <div class="placeload">
    <div
      v-for="count in repeat"
      :key="count"
      :class="[isReadyTemplate && type, display]"
      :style="{ gridTemplateColumns, gridTemplateAreas }"
      class="placeload-container"
    >
      <template v-for="item in loaders" :key="item.type">
        <template v-for="i in item.count" :key="i">
          <!-- If loader type is not ellipsis, render with this span -->
          <span
            v-if="item.type !== 'ellipsis'"
            :class="`placeload-${item.type}`"
            :style="
              !['progress-chart', 'pie-chart'].includes(props.type)
                ? `grid-area:${item.type}${i}`
                : ''
            "
           
          >
            <!-- This spans needs for avatar and input skeleton loader -->
            <span />
            <span />

            <Icon
              v-if="!isUndefined(generateIcon(item))"
              :name="generateIcon(item)!"
              size="lg"
              class="!text-slate-400"
            />
          </span>

          <!-- Otherwise, render ellipsis with this span -->
          <span v-else class="placeload-ellipsis">
            <span class="placeload-ellipsis-dot" />
            <span class="placeload-ellipsis-dot" />
            <span class="placeload-ellipsis-dot" />
          </span>
        </template>
      </template>
    </div>
  </div>
</template>
